import React from 'react';
import { Box, Typography } from '@mui/material';
import { DS } from '../../PatientForms/designTokens';
import { PreventiveMedication } from '../types';

interface HasAdditionalPreventiveMedicationHeadacheProps {
  additionalPreventiveMedications: PreventiveMedication[];
  setAdditionalPreventiveMedications: (
    medications: PreventiveMedication[]
  ) => void;
  handleFormSubmit: (e: React.FormEvent) => void;
}

const HasAdditionalPreventiveMedicationHeadache: React.FC<
  HasAdditionalPreventiveMedicationHeadacheProps
> = ({
  additionalPreventiveMedications,
  setAdditionalPreventiveMedications,
  handleFormSubmit,
}) => {
  const handleInputChange = (
    index: number,
    field: keyof PreventiveMedication,
    value: string
  ) => {
    const updatedPreventiveMedication = [...additionalPreventiveMedications];
    updatedPreventiveMedication[index][field] = value;
    setAdditionalPreventiveMedications(updatedPreventiveMedication);
  };

  const handleAddNewSet = () => {
    setAdditionalPreventiveMedications([
      ...additionalPreventiveMedications,
      {
        name: '',
        dose: '',
        effect: '',
        frequency: '',
        startYear: '',
        sideEffects: '',
        sideEffectDetails: '',
      },
    ]);
  };

  return (
    <Box sx={{ mt: 1 }}>
      {additionalPreventiveMedications.map((medication, index) => (
        // eslint-disable-next-line react/no-array-index-key -- entries have no id; the list is only appended to and edited in place
        <Box sx={{ mt: 1 }} key={index}>
          <Typography
            sx={{
              fontWeight: 600,
              color: '#374151',
              mt: 2,
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Förebyggande läkemedel {index + 2}
          </Typography>
          <input
            type="text"
            onBlur={handleFormSubmit}
            placeholder="Vilket?"
            value={medication.name}
            onChange={(e) => handleInputChange(index, 'name', e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '16px',
              fontFamily: DS.fontBody,
              marginTop: '8px',
              marginBottom: '8px',
            }}
          />
          <input
            type="text"
            onBlur={handleFormSubmit}
            placeholder="Vilken dos"
            value={medication.dose}
            onChange={(e) => handleInputChange(index, 'dose', e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '16px',
              fontFamily: DS.fontBody,
              marginTop: '8px',
              marginBottom: '8px',
            }}
          />
          <Typography
            sx={{
              display: 'block',
              color: '#374151',
              mt: 2,
              mb: 1,
              fontFamily: DS.fontBody,
              fontSize: '16px',
            }}
          >
            Hur har detta läkemedel påverkat din anfallsfrekvens?
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {[
              'Betydligt färre anfall',
              'Något färre anfall',
              'Ingen märkbar effekt',
            ].map((effect) => (
              <Box
                component="label"
                key={effect}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                <input
                  type="radio"
                  onBlur={handleFormSubmit}
                  name={`preventiveMedicationEffectivenessHeadicAdditionalMedic${index}`}
                  value={effect}
                  checked={medication.effect === effect}
                  onChange={() => handleInputChange(index, 'effect', effect)}
                  style={{ marginRight: '8px' }}
                />
                {effect}
              </Box>
            ))}
          </Box>
          <Box sx={{ mt: 2, mb: 2 }}>
            <input
              type="text"
              onBlur={handleFormSubmit}
              placeholder="Startår"
              value={medication.startYear || ''}
              onChange={(e) =>
                handleInputChange(index, 'startYear', e.target.value)
              }
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                fontSize: '16px',
                fontFamily: DS.fontBody,
                marginTop: '8px',
              }}
            />
            <Typography
              sx={{
                fontWeight: 600,
                color: '#374151',
                mb: 1,
                mt: 2,
                fontFamily: DS.fontBody,
                fontSize: '16px',
              }}
            >
              Upplever du några biverkningar av detta läkemedel?
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <input
                type="radio"
                onBlur={handleFormSubmit}
                name={`preventiveMedicationSideEffects${index}`}
                value="yes"
                checked={medication.sideEffects === 'yes'}
                onChange={() => handleInputChange(index, 'sideEffects', 'yes')}
                style={{ marginRight: '8px' }}
              />
              Ja
            </Box>
            <Box
              component="label"
              sx={{ display: 'flex', alignItems: 'center' }}
            >
              <input
                type="radio"
                onBlur={handleFormSubmit}
                name={`preventiveMedicationSideEffects${index}`}
                value="no"
                checked={medication.sideEffects === 'no'}
                onChange={() => handleInputChange(index, 'sideEffects', 'no')}
                style={{ marginRight: '8px' }}
              />
              Nej
            </Box>
          </Box>

          {medication.sideEffects === 'yes' && (
            <Box sx={{ mt: 2 }}>
              <Typography
                sx={{
                  display: 'block',
                  color: '#374151',
                  mb: 1,
                  fontFamily: DS.fontBody,
                  fontSize: '16px',
                }}
              >
                Ange biverkan:
              </Typography>
              <input
                type="text"
                onBlur={handleFormSubmit}
                value={medication.sideEffectDetails || ''}
                onChange={(e) =>
                  handleInputChange(index, 'sideEffectDetails', e.target.value)
                }
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #D1D5DB',
                  borderRadius: '6px',
                  fontSize: '16px',
                  fontFamily: DS.fontBody,
                }}
              />
            </Box>
          )}
        </Box>
      ))}
      <Box
        component="button"
        onClick={handleAddNewSet}
        sx={{
          mt: 2,
          py: 1.5,
          px: 2,
          backgroundColor: DS.deepTeal,
          color: '#FFFFFF',
          borderRadius: '6px',
          width: '100%',
          border: 'none',
          cursor: 'pointer',
          fontFamily: DS.fontBody,
          fontSize: '16px',
        }}
      >
        Lägg till ytterligare förebyggande medicin
      </Box>
    </Box>
  );
};

export default HasAdditionalPreventiveMedicationHeadache;
